import React from 'react';
import PolicyWrapper from '../components/PolicyWrapper';
import { Cookie, Settings, Shield } from 'lucide-react';

const CookiePolicy = () => {
  return (
    <PolicyWrapper title="Cookie" subtitle="Policy">
      <p className="mt-4 text-gray-700 mb-10 text-center">
        Learn how we use cookies to improve your browsing experience and provide better service.
      </p>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">What Are Cookies?</h2>
        <p className="text-gray-800">
          Cookies are small text files that are stored on your device when you visit our website. 
          They help us provide you with a better browsing experience and allow us to improve our services.
        </p>
      </div>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">How We Use Cookies</h2>
        <p className="text-gray-800 mb-4">
          We use cookies for the following purposes:
        </p>
        
        <div className="space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-red-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Shield className="text-red-600" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Essential Cookies</h3>
              <p className="text-gray-800">
                These cookies are necessary for the website to function properly. They enable basic functions 
                like page navigation, access to secure areas, and shopping cart functionality.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-red-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Settings className="text-red-600" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Analytics Cookies</h3>
              <p className="text-gray-800">
                We use analytics cookies to understand how visitors interact with our website. This helps us 
                improve our site design, content, and user experience.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-red-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Cookie className="text-red-600" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Personalization Cookies</h3>
              <p className="text-gray-800">
                These cookies remember your preferences and settings to provide a personalized experience, 
                such as remembering your vehicle model or preferred product categories.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">Specific Cookie Types</h2>
        
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Shopping Cart Cookies</h3>
            <p className="text-gray-800 mb-2">
              These cookies maintain your shopping cart contents and checkout information during your session.
            </p>
            <ul className="list-disc pl-6 space-y-1 text-gray-800">
              <li>Remember items in your cart</li>
              <li>Store shipping and billing information</li>
              <li>Maintain checkout progress</li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Analytics and Performance</h3>
            <p className="text-gray-800 mb-2">
              We use Google Analytics and similar tools to understand website usage patterns.
            </p>
            <ul className="list-disc pl-6 space-y-1 text-gray-800">
              <li>Page views and navigation patterns</li>
              <li>Popular products and categories</li>
              <li>Site performance metrics</li>
              <li>User behavior analysis</li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Preference Cookies</h3>
            <p className="text-gray-800 mb-2">
              These cookies remember your choices and preferences for a better experience.
            </p>
            <ul className="list-disc pl-6 space-y-1 text-gray-800">
              <li>Language and region preferences</li>
              <li>Vehicle model and year selections</li>
              <li>Display preferences and settings</li>
              <li>Login status and account information</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">Third-Party Cookies</h2>
        <p className="text-gray-800 mb-4">
          Some cookies are placed by third-party services that appear on our pages:
        </p>
        <ul className="list-disc pl-6 space-y-1 text-gray-800">
          <li><strong>Shopify:</strong> Our ecommerce platform uses cookies for shopping functionality</li>
          <li><strong>Google Analytics:</strong> For website analytics and performance tracking</li>
          <li><strong>Payment Processors:</strong> For secure payment processing</li>
          <li><strong>Social Media:</strong> For sharing and social media integration</li>
        </ul>
      </div>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">Managing Your Cookie Preferences</h2>
        <p className="text-gray-800 mb-4">
          You have several options for managing cookies:
        </p>
        
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Browser Settings</h3>
            <p className="text-gray-800 mb-2">
              Most web browsers allow you to control cookies through their settings:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-gray-800">
              <li>Chrome: Settings → Privacy and Security → Cookies and other site data</li>
              <li>Firefox: Options → Privacy & Security → Cookies and Site Data</li>
              <li>Safari: Preferences → Privacy → Manage Website Data</li>
              <li>Edge: Settings → Cookies and site permissions → Cookies and site data</li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Opt-Out Options</h3>
            <p className="text-gray-800 mb-2">
              You can opt out of certain types of cookies:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-gray-800">
              <li>Analytics cookies: Use browser settings or opt-out tools</li>
              <li>Advertising cookies: Use ad blocker extensions</li>
              <li>Third-party cookies: Disable in browser settings</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">Impact of Disabling Cookies</h2>
        <p className="text-gray-800 mb-4">
          Please note that disabling certain cookies may affect your experience:
        </p>
        <ul className="list-disc pl-6 space-y-1 text-gray-800">
          <li>Shopping cart may not function properly</li>
          <li>Some features may not work as expected</li>
          <li>You may need to re-enter information more frequently</li>
          <li>Personalization features may be limited</li>
        </ul>
      </div>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">Updates to This Policy</h2>
        <p className="text-gray-800">
          We may update this Cookie Policy from time to time to reflect changes in our practices 
          or for other operational, legal, or regulatory reasons. We will notify you of any material 
          changes by posting the new policy on this page.
        </p>
      </div>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">Contact Us</h2>
        <p className="text-gray-800 mb-4">
          If you have any questions about our use of cookies, please contact us:
        </p>
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-800">Taco Garage</p>
          <p className="text-gray-800">1409 Broadway, Suite 101</p>
          <p className="text-gray-800">Chula Vista, CA 91911</p>
          <p className="text-red-600">info@tacogarage.com</p>
        </div>
      </div>

      <div className="mt-12 p-6 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-sm text-gray-800">
          <strong>Last updated:</strong> {new Date().toLocaleDateString()}
        </p>
      </div>
    </PolicyWrapper>
  );
};

export default CookiePolicy;
